
interface WorkOrderPrintViewProps {
  workOrder: any
}

/** Una foto già convertita in data-URL base64, pronta per l'embedding nell'HTML di stampa. */
export interface PrintPhoto {
  id: number
  fase: string
  file_name: string
  data_url: string
  created_at?: string
}

const PRINT_FASE_ORDER: string[] = ['accettazione', 'lavorazione', 'riconsegna']
const PRINT_FASE_LABELS: Record<string, string> = {
  accettazione: 'Accettazione',
  lavorazione: 'Lavorazione',
  riconsegna: 'Riconsegna',
}

const statoLabels: Record<string, string> = {
  bozza: 'Bozza',
  approvata: 'Approvata',
  'in lavorazione': 'In Lavorazione',
  completata: 'Completata',
  annullata: 'Annullata',
}

const rdaStatusLabels: Record<number, string> = {
  1: 'Bozza',
  2: 'In Attesa Approvazione',
  3: 'Approvata',
  4: 'Rifiutata',
  5: 'Ordinata',
  6: 'Ricevuta',
  7: 'Annullata',
  8: 'Rifiutata',
  19: 'Rifiutata',
}

const modalitaLabels: Record<number, string> = {
  1: 'Riparazione',
  2: 'Acquisto',
}

const ramoSinistroLabels: Record<number, string> = {
  1: 'RCA - Responsabilità Civile Auto',
  2: 'ARD - Assistenza e Recupero Danni',
  3: 'Kasko',
  4: 'Mini Kasko / Collisione',
  5: 'Cristalli',
  6: 'Incendio',
  7: 'Furto',
  8: 'Eventi Naturali',
  9: 'Atti Vandalici',
  10: 'Tutela Legale',
  11: 'Infortuni del Conducente',
  12: 'Privato / Senza Assicurazione',
}

function fmt(dateStr?: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('it-IT')
}

function esc(v: any): string {
  if (v == null) return '—'
  return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function buildPrintHTML(wo: any, rdas: any[] = [], printData: any[] = [], photos: PrintPhoto[] = []): string {
  const interventions: any[] = wo.interventions ?? []
  const allRDA: any[] = ((wo.purchase_requests as any[]) ?? []).filter((rda: any) =>
    !rda.intervention_id
  )
  const printedAt = new Date().toLocaleString('it-IT')

  const rdaByIntervention: Record<number, any> = {}
  for (const rda of rdas) {
    rdaByIntervention[rda.intervention_id] = rda
  }

  // printData: PurchaseRequest+RDO indexed by intervention_id
  const printPrByIntervention: Record<number, any[]> = {}
  for (const pr of printData) {
    if (pr.intervention_id != null) {
      if (!printPrByIntervention[pr.intervention_id]) printPrByIntervention[pr.intervention_id] = []
      printPrByIntervention[pr.intervention_id].push(pr)
    }
  }

  const interventionRows = interventions.length === 0
    ? `<tr><td colspan="5" style="text-align:center;color:#999;font-style:italic;">Nessun intervento registrato</td></tr>`
    : interventions.map(i => {
        // Usa printData se disponibile, altrimenti fallback su purchase_requests dell'intervento
        // Escludi PRs rifiutati o annullati dalla stampa
        const STATI_ESCLUSI = new Set(['rifiutata', 'annullata'])
        const STATI_AVANZATI = new Set(['ordinata', 'ricevuta'])
        const allPrs: any[] = (printPrByIntervention[i.id] ?? i.purchase_requests ?? [])
          .filter((pr: any) => !STATI_ESCLUSI.has((pr.purchase_request_status_nome ?? '').toLowerCase()))

        // Se esiste almeno una RDA in stato avanzato, le RDA in Bozza non vanno stampate
        const hasAvanzata = allPrs.some(
          (pr: any) => STATI_AVANZATI.has((pr.purchase_request_status_nome ?? '').toLowerCase())
        )
        const prsForIntervention: any[] = hasAvanzata
          ? allPrs.filter((pr: any) =>
              STATI_AVANZATI.has((pr.purchase_request_status_nome ?? '').toLowerCase())
            )
          : allPrs

        const blueRows = prsForIntervention.flatMap((pr: any) => {
          // Mostra articoli RDA
          return (pr.items ?? []).map((item: any) => {
            const statusLabel = rdaStatusLabels[pr.purchase_request_status_id] ?? '—'
            return {
              descrizione: item.descrizione,
              quantita: item.quantita,
              fornitore: null,
              stato: item.ricevuto
                ? `<span style="color:#389e0d;font-weight:bold;">✓ Ricevuto</span>`
                : `<span style="color:#d46b08;">${esc(statusLabel)}</span>`,
              data: item.data_ricezione_item
                ? new Date(item.data_ricezione_item).toLocaleDateString('it-IT')
                : '—',
            }
          })
        })

        const rdaItemsRow = blueRows.length > 0 ? `
          <tr>
            <td></td>
            <td colspan="4" style="background:#f0f5ff;padding:4px 10px;border-top:none;">
              <div style="font-size:9px;font-weight:bold;color:#1d3557;margin-bottom:3px;">Articoli da acquistare:</div>
              <table style="margin:0;width:100%;font-size:9px;">
                ${blueRows.map((row: any) => `
                  <tr style="background:transparent;">
                    <td style="border:none;padding:1px 4px;color:#333;">· ${esc(row.descrizione)}</td>
                    <td style="border:none;padding:1px 4px;text-align:center;white-space:nowrap;width:30px;">${row.quantita ?? '—'}</td>
                    <td style="border:none;padding:1px 4px;text-align:center;white-space:nowrap;width:120px;">${row.stato}</td>
                    <td style="border:none;padding:1px 4px;text-align:center;white-space:nowrap;width:75px;color:#666;">${row.data}</td>
                  </tr>`).join('')}
              </table>
            </td>
          </tr>` : ''
        const statoBadge = i.stato_intervento?.nome
          ? `<span style="font-size:9px;background:#f0f5ff;border:1px solid #adc6ff;border-radius:3px;padding:1px 5px;color:#2f54eb;">${esc(i.stato_intervento.nome)}</span>`
          : ''
        const officinaBadge = i.workshop_nome
          ? `<span style="font-size:9px;background:#f6ffed;border:1px solid #b7eb8f;border-radius:3px;padding:1px 5px;color:#389e0d;">${esc(i.workshop_nome)}</span>`
          : ''
        const badges = [statoBadge, officinaBadge].filter(Boolean).join('')
        return `
        <tr>
          <td style="text-align:center">${esc(i.progressivo)}</td>
          <td>
            ${esc(i.descrizione_intervento)}
            ${i.note_intervento ? `<div style="font-size:9px;color:#595959;margin-top:2px;font-style:italic;">${esc(i.note_intervento)}</div>` : ''}
            ${badges ? `<div style="margin-top:3px;display:flex;gap:5px;flex-wrap:wrap;">${badges}</div>` : ''}
          </td>
          <td style="text-align:center">${esc(modalitaLabels[i.modalita_risoluzione_id] ?? '—')}</td>
          <td style="text-align:center">${i.durata_stimata != null ? `${i.durata_stimata}h` : '—'}</td>
          <td style="text-align:center">${i.ore_effettive != null ? `${i.ore_effettive}h` : '—'}</td>
        </tr>${rdaItemsRow}`
      }).join('')

  const rdaSection = allRDA.length === 0 ? '' : `
    <div class="section-title" style="margin-top:20px">Richieste di Offerta — RDO (${allRDA.length})</div>
    ${allRDA.map((rda: any) => {
      const itemRows: any[] = rda.items ?? []
      const itemsHTML = itemRows.length === 0
        ? `<tr><td colspan="5" style="text-align:center;color:#999;font-style:italic;">Nessun articolo</td></tr>`
        : itemRows.map((item: any) => {
            const fornitore = item.offerta_fornitore || rda.fornitore
            const dataRicezione = item.data_ricezione_item
              ? new Date(item.data_ricezione_item).toLocaleDateString('it-IT')
              : '—'
            const itemStato = item.ricevuto
              ? `<span style="color:#389e0d;font-weight:bold;">✓ Ricevuto</span>`
              : `<span style="color:#d46b08;">${esc(rdaStatusLabels[rda.purchase_request_status_id] ?? '—')}</span>`
            return `
          <tr>
            <td>
              ${esc(item.descrizione)}
              ${fornitore ? `<span style="font-size:9px;color:#888;margin-left:6px;">· ${esc(fornitore)}</span>` : ''}
            </td>
            <td style="text-align:center">${esc(item.quantita)}</td>
            <td style="text-align:center">${esc(item.unita_misura)}</td>
            <td style="text-align:center;white-space:nowrap;font-size:10px;">${itemStato}</td>
            <td style="text-align:center;white-space:nowrap;font-size:10px;">${dataRicezione}</td>
          </tr>`}).join('')
      return `
      <div style="margin-top:10px;border:1px solid #ddd;border-radius:4px;overflow:hidden;">
        <div style="background:#f5f5f5;padding:6px 10px;display:flex;gap:16px;align-items:center;border-bottom:1px solid #ddd;">
          <strong style="color:#1890ff">${esc(rda.codice)}</strong>
          <span style="background:#e6f7ff;border:1px solid #91d5ff;border-radius:3px;padding:1px 6px;font-size:10px;">${esc(rdaStatusLabels[rda.purchase_request_status_id])}</span>
          ${rda.fornitore ? `<span style="color:#555">${esc(rda.fornitore)}</span>` : ''}
          ${rda.descrizione ? `<span style="color:#333">${esc(rda.descrizione)}</span>` : ''}
        </div>
        <table style="margin:0">
          <thead>
            <tr>
              <th>Articolo</th>
              <th style="width:50px">Qtà</th>
              <th style="width:45px">U.M.</th>
              <th style="width:80px">Stato</th>
              <th style="width:80px">Data cons.</th>
            </tr>
          </thead>
          <tbody>${itemsHTML}</tbody>
        </table>
      </div>`
    }).join('')}`

  const sinistroSection = (wo.sinistro || wo.numero_sinistro || wo.compagnia_sinistro) ? `
    <div class="info-box" style="margin-top:10px">
      <div class="section-title">Dettagli Sinistro Amministrativo</div>
      <table class="kv">
        <tbody>
          ${wo.ramo_sinistro_id ? `<tr><td>Ramo Sinistro</td><td>${esc(ramoSinistroLabels[wo.ramo_sinistro_id] ?? String(wo.ramo_sinistro_id))}</td></tr>` : ''}
          <tr><td>N. Sinistro</td><td>${esc(wo.numero_sinistro)}</td></tr>
          <tr><td>Compagnia</td><td>${esc(wo.compagnia_sinistro)}</td></tr>
          ${wo.compagnia_debitrice_sinistro ? `<tr><td>Compagnia Debitrice</td><td>${esc(wo.compagnia_debitrice_sinistro)}</td></tr>` : ''}
          ${wo.scoperto != null ? `<tr><td>Scoperto</td><td>${esc(wo.scoperto)}%</td></tr>` : ''}
          ${wo.franchigia != null ? `<tr><td>Franchigia</td><td>${esc(wo.franchigia)} €</td></tr>` : ''}
          ${wo.legale ? `<tr><td>Legale</td><td>${esc(wo.legale)}</td></tr>` : ''}
          ${wo.autorita ? `<tr><td>Autorità</td><td>${esc(wo.autorita)}</td></tr>` : ''}
        </tbody>
      </table>
    </div>` : ''

  // ── Sezione foto (solo se ci sono foto da includere) ──────────────────────
  const photosSection = photos.length === 0 ? '' : (() => {
    const groups = PRINT_FASE_ORDER
      .map(fase => ({
        fase,
        label: PRINT_FASE_LABELS[fase] ?? fase,
        items: photos.filter(p => p.fase === fase),
      }))
      .filter(g => g.items.length > 0)

    const groupsHTML = groups.map(g => {
      // Gruppi brevi (≤4 foto = max 2 righe) evitano il salto pagina al proprio interno
      const avoidBreak = g.items.length <= 4
        ? ' style="break-inside:avoid;page-break-inside:avoid;"'
        : ''
      const itemsHTML = g.items.map(p => {
        const dateStr = p.created_at
          ? new Date(p.created_at).toLocaleDateString('it-IT')
          : ''
        const caption = [esc(p.file_name), dateStr].filter(Boolean).join(' · ')
        return `<div class="foto-item">
          <img src="${p.data_url}" alt="${esc(p.file_name)}" class="foto-thumb" />
          ${caption ? `<div class="foto-caption">${caption}</div>` : ''}
        </div>`
      }).join('')
      return `
        <div class="foto-fase-group"${avoidBreak}>
          <div class="foto-fase-label">${esc(g.label)}</div>
          <div class="foto-grid">${itemsHTML}</div>
        </div>`
    }).join('')

    return `
      <div class="section-title" style="margin-top:20px">Foto (${photos.length})</div>
      ${groupsHTML}
    `
  })()

  const danoSection = wo.valutazione_danno ? `
    <div class="info-box" style="margin-top:10px">
      <div class="section-title">Descrizione Danno</div>
      <p style="white-space:pre-wrap;font-size:11px;color:#333;margin:0">${esc(wo.valutazione_danno)}</p>
    </div>` : ''

  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8" />
  <title>Scheda Lavoro ${wo.numero_scheda ?? wo.id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: Arial, sans-serif;
      font-size: 11px;
      color: #111;
      background: #e8e8e8;
    }

    /* ── Print toolbar (hidden on actual print) ── */
    #print-toolbar {
      position: fixed;
      top: 0; left: 0; right: 0;
      background: #1890ff;
      color: #fff;
      padding: 10px 20px;
      display: flex;
      align-items: center;
      gap: 12px;
      z-index: 9999;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    }
    #print-toolbar span { font-size: 14px; font-weight: bold; flex: 1; }
    #print-toolbar button {
      padding: 6px 20px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 13px;
      font-weight: bold;
    }
    #btn-print { background: #fff; color: #1890ff; }
    #btn-close { background: rgba(255,255,255,0.2); color: #fff; border: 1px solid rgba(255,255,255,0.5) !important; }
    #btn-print:hover { background: #e6f7ff; }
    #btn-close:hover { background: rgba(255,255,255,0.3); }

    /* ── Page wrapper ── */
    #page-wrapper { padding: 60px 20px 20px 20px; min-height: 100vh; }

    /* ── Document card ── */
    .doc-page {
      background: #fff;
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 15mm 15mm 20mm 15mm;
      box-shadow: 0 4px 20px rgba(0,0,0,0.2);
    }

    @page { size: A4 portrait; margin: 12mm 15mm; }
    @media print {
      #print-toolbar { display: none !important; }
      #page-wrapper { padding: 0; background: none; }
      body { background: none; }
      .doc-page { box-shadow: none; width: 100%; min-height: unset; padding: 0; margin: 0; }
    }

    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
    .company { font-size: 18px; font-weight: bold; }
    .company-sub { font-size: 10px; color: #666; margin-top: 2px; }
    .doc-title { font-size: 14px; font-weight: bold; letter-spacing: 1px; color: #333; text-align: right; }
    .doc-number { font-size: 20px; font-weight: bold; color: #1890ff; text-align: right; margin-top: 2px; }
    .doc-state { font-size: 10px; color: #555; text-align: right; margin-top: 2px; }
    .divider { border-top: 2px solid #1890ff; margin: 8px 0 12px 0; }

    .info-grid { display: flex; gap: 16px; margin-bottom: 8px; }
    .info-box { flex: 1; border: 1px solid #d9d9d9; border-radius: 4px; padding: 10px 12px; background: #fafafa; }

    .section-title {
      font-size: 11px; font-weight: bold; text-transform: uppercase;
      letter-spacing: 0.5px; color: #555;
      border-bottom: 1px solid #e8e8e8; padding-bottom: 4px; margin-bottom: 6px;
    }

    .kv { width: 100%; border-collapse: collapse; font-size: 11px; }
    .kv td:first-child { color: #666; width: 42%; padding: 2px 8px 2px 0; white-space: nowrap; }
    .kv td:last-child { color: #111; padding: 2px 0; }

    table { width: 100%; border-collapse: collapse; font-size: 10.5px; margin-top: 6px; }
    th { background: #f0f0f0; border: 1px solid #ccc; padding: 5px 7px; text-align: left; font-weight: bold; color: #333; }
    td { border: 1px solid #ddd; padding: 4px 7px; vertical-align: top; }
    tr:nth-child(even) td { background: #fafafa; }

    .signatures { display: flex; gap: 40px; margin-top: 30px; padding-top: 10px; }
    .sig-box { flex: 1; }
    .sig-line { border-bottom: 1px solid #333; height: 40px; margin-bottom: 4px; }
    .sig-label { font-size: 10px; color: #555; text-align: center; }

    .footer {
      margin-top: 16px;
      font-size: 9px; color: #999; text-align: center;
      border-top: 1px solid #e8e8e8; padding-top: 4px;
    }

    /* ── Sezione foto ── */
    .foto-fase-group { margin-bottom: 16px; }
    .foto-fase-label {
      font-size: 10px; font-weight: bold; color: #555;
      text-transform: uppercase; letter-spacing: 0.4px;
      border-bottom: 1px solid #e8e8e8; padding-bottom: 3px; margin-bottom: 8px;
      break-after: avoid; page-break-after: avoid;
    }
    .foto-grid { display: flex; flex-wrap: wrap; gap: 8px; }
    /* Ogni foto occupa metà larghezza (due per riga) — su A4 ≈ 86mm ciascuna */
    .foto-item {
      width: calc(50% - 4px);
      break-inside: avoid; page-break-inside: avoid;
    }
    .foto-thumb {
      width: 100%; height: auto;
      /* Cap verticale: una foto portrait non supera metà pagina */
      max-height: 140mm;
      object-fit: contain;
      border: 1px solid #ddd; border-radius: 3px;
      display: block;
    }
    .foto-caption {
      font-size: 8px; color: #888; margin-top: 3px;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    @media print {
      .foto-item { break-inside: avoid; page-break-inside: avoid; }
      .foto-fase-label { break-after: avoid; page-break-after: avoid; }
    }
  </style>
</head>
<body>

  <div id="print-toolbar">
    <span>Anteprima Stampa — Scheda ${wo.numero_scheda ?? wo.id}</span>
    <button id="btn-print" onclick="window.print()">🖨 Stampa</button>
    <button id="btn-close" onclick="window.close()">✕ Chiudi</button>
  </div>

  <div id="page-wrapper">
    <div class="doc-page">

      <div class="header">
        <div>
          <div class="company">Garage Management</div>
          <div class="company-sub">Sistema di Gestione Officina</div>
        </div>
        <div>
          <div class="doc-title">SCHEDA DI LAVORO</div>
          <div class="doc-number">N. ${esc(wo.numero_scheda ?? wo.id)}</div>
          <div class="doc-state">Stato: <strong>${esc(statoLabels[wo.stato] ?? wo.stato)}</strong></div>
        </div>
      </div>

      <div class="divider"></div>

      <div class="info-grid">
        <div class="info-box">
          <div class="section-title">Dati Scheda</div>
          <table class="kv">
            <tbody>
              <tr><td>Data appuntamento</td><td>${fmt(wo.data_appuntamento)}</td></tr>
              <tr><td>Fine prevista</td><td>${fmt(wo.data_fine_prevista)}</td></tr>
              <tr><td>Priorità</td><td style="text-transform:capitalize">${esc(wo.priorita)}</td></tr>
              ${wo.costo_stimato != null ? `<tr><td>Costo stimato</td><td>${Number(wo.costo_stimato).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</td></tr>` : ''}
            </tbody>
          </table>
        </div>
        <div class="info-box">
          <div class="section-title">Cliente</div>
          <table class="kv">
            <tbody>
              <tr><td>Nome</td><td><strong>${esc(wo.customer_nome)}</strong></td></tr>
              ${wo.customer_telefono ? `<tr><td>Telefono</td><td>${esc(wo.customer_telefono)}</td></tr>` : ''}
              ${wo.customer_email ? `<tr><td>Email</td><td>${esc(wo.customer_email)}</td></tr>` : ''}
            </tbody>
          </table>
          <div class="section-title" style="margin-top:10px">Veicolo</div>
          <table class="kv">
            <tbody>
              <tr><td>Marca / Modello</td><td><strong>${esc([wo.vehicle_marca, wo.vehicle_modello].filter(Boolean).join(' ') || '—')}</strong></td></tr>
              <tr><td>Targa</td><td><strong>${esc(wo.vehicle_targa)}</strong></td></tr>
              ${wo.vehicle_anno ? `<tr><td>Anno</td><td>${esc(wo.vehicle_anno)}</td></tr>` : ''}
              ${wo.vehicle_colore ? `<tr><td>Colore</td><td>${esc(wo.vehicle_colore)}</td></tr>` : ''}
              ${wo.vehicle_km ? `<tr><td>Km</td><td>${Number(wo.vehicle_km).toLocaleString('it-IT')}</td></tr>` : ''}
            </tbody>
          </table>
        </div>
      </div>

      ${danoSection}
      ${sinistroSection}

      <div class="section-title" style="margin-top:20px">Interventi Tecnici (${interventions.length})</div>
      <table>
        <thead>
          <tr>
            <th style="width:35px">#</th>
            <th>Descrizione</th>
            <th style="width:90px">Modalità</th>
            <th style="width:80px">Ore Stim.</th>
            <th style="width:80px">Ore Eff.</th>
          </tr>
        </thead>
        <tbody>${interventionRows}</tbody>
      </table>

      ${rdaSection}
      ${photosSection}

      <div class="signatures">
        <div class="sig-box">
          <div class="sig-line"></div>
          <div class="sig-label">Firma Cliente</div>
        </div>
        <div class="sig-box">
          <div class="sig-line"></div>
          <div class="sig-label">Firma Tecnico Responsabile</div>
        </div>
      </div>

      <div class="footer">
        Stampato il ${printedAt} &nbsp;|&nbsp; Scheda N. ${esc(wo.numero_scheda ?? wo.id)}
      </div>

    </div>
  </div>
</body>
</html>`
}

export function printWorkOrder(workOrder: any, rdas: any[] = [], printData: any[] = [], photos: PrintPhoto[] = []) {
  const html = buildPrintHTML(workOrder, rdas, printData, photos)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const win = window.open(url, '_blank', 'width=960,height=850')
  if (!win) {
    URL.revokeObjectURL(url)
    alert("Il browser ha bloccato l'apertura della finestra di anteprima. Abilita i popup per questo sito.")
    return
  }
  // Revoke after the window has loaded the content
  win.addEventListener('load', () => URL.revokeObjectURL(url), { once: true })
  win.focus()
}

// Componente vuoto — la stampa avviene tramite printWorkOrder()
export function WorkOrderPrintView(_props: WorkOrderPrintViewProps) {
  return null
}
